import React from "react";
import { Link } from "react-router-dom";
import MarketingShell from "./MarketingShell";
import { GENERAL_CONTACT_EMAIL } from "./constants";

/**
 * Plain-language legal pages. Real legal review is pending — every page
 * shows a "Last updated" date and a note that this is a working draft.
 * Replace with reviewed legal text before public launch on sistersignal.org.
 */
function Page({ title, kicker, children, testid }) {
  return (
    <MarketingShell>
      <article className="max-w-3xl mx-auto px-6 sm:px-10 py-20" data-testid={testid}>
        <p className="font-script text-2xl text-brand">{kicker}</p>
        <h1 className="mt-2 font-serif text-5xl sm:text-6xl leading-tight">{title}</h1>
        <p className="mt-4 text-xs uppercase tracking-widest text-foreground/55">
          Last updated: February 2026 · Working draft pending legal review
        </p>
        <div className="prose prose-neutral max-w-none mt-8 text-foreground/85 leading-relaxed
                        prose-headings:font-serif prose-headings:text-foreground
                        prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-2
                        prose-h3:text-xl prose-h3:mt-6
                        prose-strong:text-foreground
                        prose-a:text-brand">
          {children}
        </div>
        <Link to="/" className="mt-12 inline-block text-sm text-brand hover:underline">← Back to home</Link>
      </article>
    </MarketingShell>
  );
}

export function PrivacyPolicy() {
  return (
    <Page title="Privacy Policy" kicker="how we treat your data" testid="page-privacy">
      <p>
        Sister Signal is a safety tool. We treat your privacy as a core feature of that safety —
        not a checkbox.
      </p>
      <h2>What we collect</h2>
      <ul>
        <li><strong>Anonymous device identifier</strong> — a random ID we generate to scope your data to your device. No personal information attached.</li>
        <li><strong>Email (optional)</strong> — only if you choose to add one for cross-device sync or password recovery.</li>
        <li><strong>Content you create</strong> — onboarding choices, decode entries, incident logs, reflections, trusted contacts. Stored in your account and never shared with other users.</li>
        <li><strong>Minimal analytics</strong> — anonymised page views to understand which features help. No third-party trackers.</li>
      </ul>
      <h2>What we never do</h2>
      <ul>
        <li>We do not sell your data.</li>
        <li>We do not show ads.</li>
        <li>We do not share your content with anyone outside Sister Signal.</li>
        <li>We do not track your location passively.</li>
      </ul>
      <h2>Trusted Circle SMS</h2>
      <p>
        When you send an alert to a trusted contact, the message is sent through Twilio. The first
        message each contact receives includes a clear opt-out instruction. We retain a log of
        attempted sends for your own audit on the Alerts screen in the app.
      </p>
      <h2>Your rights</h2>
      <p>
        You can export, delete, or pause your data at any time from within the app's Settings.
        For questions or requests, email us at <a href={`mailto:${GENERAL_CONTACT_EMAIL}`}>{GENERAL_CONTACT_EMAIL}</a>.
      </p>
      <h2>Children</h2>
      <p>
        Sister Signal is not directed at children under 13. We do not knowingly collect
        information from anyone under 13. Users between 13 and 18 should review this policy with
        a trusted adult where possible.
      </p>
      <p className="text-sm italic text-foreground/55 mt-12">
        This is a plain-language summary. A fully reviewed policy will be published before formal launch.
      </p>
    </Page>
  );
}

export function Terms() {
  return (
    <Page title="Terms of Service" kicker="the agreement, plainly" testid="page-terms">
      <p>
        By using Sister Signal you agree to these terms. They're written in plain language because
        you have enough confusing fine print in your life already.
      </p>
      <h2>What Sister Signal is — and isn't</h2>
      <p>
        Sister Signal is an emotional safety companion. It provides education, reflection tools,
        and a private library of relationship-pattern information. <strong>Sister Signal is not a
        therapist, attorney, or emergency service.</strong> Nothing in the app constitutes medical,
        legal, or psychological advice.
      </p>
      <h2>If you are in immediate danger</h2>
      <p>
        Please call <strong>911</strong>. The National Domestic Violence Hotline is available 24/7 at
        <strong> 1-800-799-7233</strong>.
      </p>
      <h2>Accounts</h2>
      <p>
        You can use Sister Signal anonymously. If you add an email, you're responsible for keeping
        your credentials safe. We will never ask for your password.
      </p>
      <h2>Acceptable use</h2>
      <p>
        Don't use Sister Signal to harm another person. Don't impersonate a survivor or advocate.
        Don't try to access another user's data. We may suspend accounts that do these things.
      </p>
      <h2>Donations</h2>
      <p>
        Donations are processed by Stripe. They are not tax-deductible unless explicitly stated.
        Recurring donations can be cancelled any time from your account.
      </p>
      <h2>Changes</h2>
      <p>
        We may update these terms. If we make a material change, we'll surface it in the app
        before it takes effect.
      </p>
      <p className="text-sm italic text-foreground/55 mt-12">
        This is a working draft pending legal review. Final terms will be published before launch.
      </p>
    </Page>
  );
}

export function Contact() {
  return (
    <Page title="Contact" kicker="get in touch" testid="page-contact">
      <p>
        We'd love to hear from you. Choose the right inbox below so your message reaches the
        person who can help fastest.
      </p>
      <ul>
        <li><strong>General</strong> — <a href={`mailto:${GENERAL_CONTACT_EMAIL}`}>{GENERAL_CONTACT_EMAIL}</a></li>
        <li><strong>Press & media</strong> — <Link to="/press">/press</Link></li>
        <li><strong>Partner inquiries</strong> — <Link to="/partners">/partners</Link></li>
        <li><strong>Support inside the app</strong> — open <Link to="/app">the app</Link> and tap Settings → Help</li>
      </ul>
      <h2>Not an emergency line</h2>
      <p>
        Sister Signal is not a crisis service. If you are in immediate danger, please call
        <strong> 911</strong> or the National Domestic Violence Hotline at
        <strong> 1-800-799-7233</strong>.
      </p>
    </Page>
  );
}
